import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';

import { hrApi } from '@/api';
import { Button } from '@/components/ui/Button';
import { DateTimePicker } from '@/components/ui/DateTimePicker';
import { Skeleton } from '@/components/ui/Skeleton';
import {
  HrAttendanceOvertimeFields,
} from '@/modules/hr/components/HrAttendanceOvertimeFields';
import { tierOptions } from '@/lib/query';
import type { HrAttendance } from '@/types/hr';
import { showApiError } from '@/utils/apiError';
import { toUtcIsoStringFromPicker, utcIsoToCompanyLocalPicker } from '@/utils/format';
import { notify } from '@/core/toast/notify';

interface Props {
  attendanceId: number;
  /** Pre-loaded row from list/calendar — skips fetch when provided. */
  initialAttendance?: HrAttendance | null;
  /** When true, allow edit even if approvalStatus has not synced to RETURNED yet. */
  approvalReturned?: boolean;
  onSaved?: () => void;
  onCancel?: () => void;
}

export function ReturnedAttendanceEditPanel({
  attendanceId,
  initialAttendance,
  approvalReturned = false,
  onSaved,
  onCancel,
}: Props) {
  const { t } = useTranslation('workflow');
  const [checkIn, setCheckIn] = useState('');
  const [checkOut, setCheckOut] = useState('');
  const [overtimeType, setOvertimeType] = useState('');
  const [saving, setSaving] = useState(false);

  const { data: fetchedAttendance, isLoading } = useQuery({
    queryKey: ['hr', 'attendance', attendanceId],
    queryFn: () => hrApi.getAttendance(attendanceId),
    enabled: attendanceId > 0 && initialAttendance == null,
    ...tierOptions('operational'),
  });

  const attendance = initialAttendance ?? fetchedAttendance ?? null;

  useEffect(() => {
    if (!attendance) return;
    setCheckIn(attendance.checkIn ? utcIsoToCompanyLocalPicker(attendance.checkIn) : '');
    setCheckOut(attendance.checkOut ? utcIsoToCompanyLocalPicker(attendance.checkOut) : '');
    setOvertimeType(attendance.overtimeType ?? '');
  }, [attendance]);

  const save = async () => {
    if (!checkIn.trim()) {
      notify.error(t('errors.checkInTimeIsRequired'));
      return;
    }
    if (checkOut && checkOut <= checkIn) {
      notify.error(t('errors.checkOutMustBeAfterCheckIn'));
      return;
    }
    if (!attendance) return;
    setSaving(true);
    try {
      await hrApi.updateAttendance(attendanceId, {
        employeeId: attendance.employeeId,
        checkIn: toUtcIsoStringFromPicker(checkIn.trim()),
        checkOut: checkOut.trim() ? toUtcIsoStringFromPicker(checkOut.trim()) : undefined,
        shiftId: attendance.shiftId,
        overtimeType: checkOut && overtimeType ? overtimeType : undefined,
      });
      notify.t.success('toast.attendanceUpdatedResubmitWhenReady', { ns: 'workflow' });
      onSaved?.();
    } catch (err) {
      showApiError(err, t('errors.failedToUpdateAttendance'));
    } finally {
      setSaving(false);
    }
  };

  if (isLoading && !attendance) {
    return <Skeleton className="h-32 w-full rounded-lg" />;
  }

  if (!attendance) {
    return <p className="text-sm text-slate-500">Attendance record not found.</p>;
  }

  const editable = approvalReturned || (attendance.approvalStatus ?? '').toUpperCase() === 'RETURNED';

  if (!editable) {
    return (
      <p className="text-sm text-slate-500">
        This attendance is no longer returned for edits (status: {attendance.approvalStatus ?? '—'}).
      </p>
    );
  }

  return (
    <div className="space-y-3 rounded-lg border border-slate-200 p-3 dark:border-slate-700">
      <h5 className="text-sm font-medium text-slate-800 dark:text-slate-200">Edit attendance record</h5>
      <DateTimePicker
        label={t('labels.checkIn')}
        value={checkIn}
        onChange={setCheckIn}
        required
      />
      <DateTimePicker
        label={t('labels.checkOut')}
        value={checkOut}
        onChange={setCheckOut}
        min={checkIn || undefined}
        disabled={!checkIn}
      />
      <HrAttendanceOvertimeFields
        checkIn={checkIn}
        checkOut={checkOut}
        overtimeType={overtimeType}
        onOvertimeTypeChange={setOvertimeType}
      />
      <div className="flex flex-wrap gap-2">
        <Button size="sm" loading={saving} onClick={() => void save()}>
          Save changes
        </Button>
        {onCancel ? (
          <Button size="sm" variant="ghost" onClick={onCancel}>
            Cancel
          </Button>
        ) : null}
      </div>
    </div>
  );
}
